/**
 * Alert tone previews (#232).
 *
 * One map from a canonical alert channel to THE TONE THAT CHANNEL ACTUALLY
 * EMITS. Every preview button in the Settings panel resolves through here, so
 * a preview cannot drift from the real sound.
 *
 * That drift is why this file exists. The retired `organisms/SoundSettingsPanel`
 * hand-wired its own preview per row, and its single "Queue front alert" row
 * previewed `playQueueFrontTone` — a DORMANT tone with no emit site anywhere —
 * while the Position Tracker actually emits the warning / critical / fell-behind
 * trio. A trader auditioning their queue alert heard a sound the product never
 * plays.
 *
 * Entries are the exported tone functions themselves, never re-declared voices,
 * so "the previewed sound is the emitted sound" holds by construction rather
 * than by review.
 *
 * Deliberately `Partial`: only channels that own a tone appear. Dormant channels
 * have none, and since #233 a channel can be active-but-silent (the flash-only
 * velocity cues). `alertPreview.test.ts` pins the map against
 * `SOUND_ALERT_CHANNEL_IDS` in both directions, so adding a sounding channel
 * without a preview — or reviving a dormant one — fails there instead of
 * shipping a dead or lying button.
 */

import type { AlertChannelId } from '@/lib/alerts/registry';
import {
  playFillTone,
  playKaChingTone,
  playWhaleTone,
  playOrderPlacedTone,
  playOrderRejectedTone,
  playQueueWarningTone,
  playQueueCriticalTone,
  playFellBehindTone,
  playDivergenceTone,
} from '@/lib/audio/tradeAudio';
import { playBootTone } from '@/lib/audio/defenseAudio';

/**
 * A previewable tone, taking the channel's own volume multiplier.
 *
 * The multiplier ALONE — never the effective (master x channel) product:
 * `audioContext` applies master internally, so passing the product would apply
 * master twice. This mirrors the contract #233 documented on `getChannelVolume`.
 */
export type PreviewTone = (channelVolume: number) => void;

export const ALERT_PREVIEW_TONES: Partial<Record<AlertChannelId, PreviewTone>> = {
  fill: playFillTone,
  firstFill: playKaChingTone,
  whaleFill: playWhaleTone,
  orderPlaced: playOrderPlacedTone,
  orderRejected: playOrderRejectedTone,
  boot: playBootTone,
  queueWarning: playQueueWarningTone,
  queueCritical: playQueueCriticalTone,
  fellBehind: playFellBehindTone,
  divergence: playDivergenceTone,
  // The live-game channel is a GATE on the divergence chime, not a sound of its
  // own: the emit site rings `playDivergenceTone(divergenceCh.volume)` and reads
  // this channel only as a boolean. Mapped for completeness — its row renders no
  // preview button and no volume slider, because it owns neither.
  divergenceLiveGame: playDivergenceTone,
};
